import { useEffect, useState } from 'react'
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSupportClinicBrandCommunicationSummary } from 'src/supportPortal/services/clinicBrandCommunicationDashboardService'
import ClinicBrandCommunicationOverview from './ClinicBrandCommunicationOverview'

/**
 * Lazy communications overview for a single clinic brand. The summary is only fetched when the
 * drawer is opened (button click), instead of eagerly for every brand on the clinics listing.
 */
export default function ClinicBrandCommunicationsDrawer({ open, onClose, brand }) {
  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState(null)
  const brandId = brand?.id

  useEffect(() => {
    if (!open || !brandId) return undefined
    let cancelled = false
    async function load() {
      setLoading(true)
      setSummary(null)
      try {
        const resp = await getSupportClinicBrandCommunicationSummary([brandId])
        if (cancelled) return
        if (resp && isCommonResponseSuccessfulV2(resp)) {
          const summaries =
            getDataMapFromCommonResponse(resp)?.clinicBrandCommunicationSummary || []
          setSummary(summaries.find((item) => item?.clinicBrandId === brandId) || summaries[0] || null)
        } else {
          setSummary(null)
        }
      } catch (error) {
        if (!cancelled) setSummary(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [open, brandId])

  return (
    <Drawer
      anchor="right"
      open={Boolean(open)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 680, md: 820 }, maxWidth: '100%' } }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Communications Overview
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {brand?.title || 'Clinic brand'}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      <Box sx={{ p: 2, overflowY: 'auto' }}>
        <ClinicBrandCommunicationOverview summary={summary} loading={loading} />
      </Box>
    </Drawer>
  )
}
